class AudioPlayerManager {
  constructor() {
    this.audio = null;
    this.activePlayer = null;
    this.scrubbingPlayer = null;
    this.peaksCache = new Map();
    this.decodeContext = null;

    document.addEventListener('click', this.handleClick.bind(this));
    document.addEventListener('pointerdown', this.handlePointerDown.bind(this));
    document.addEventListener('pointermove', this.handlePointerMove.bind(this));
    document.addEventListener('pointerup', this.handlePointerUp.bind(this));

    this.waveObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        this.waveObserver.unobserve(entry.target);
        this.loadWave(entry.target);
      });
    }, { rootMargin: '200px' });

    const observeAll = () => {
      document.querySelectorAll('[data-audio-player]:not([data-wave-observed])').forEach((player) => {
        if (!player.querySelector('[data-audio-wave]')) return;
        player.setAttribute('data-wave-observed', '');
        this.waveObserver.observe(player);
      });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeAll);
    } else {
      observeAll();
    }
    new MutationObserver(observeAll).observe(document.documentElement, { childList: true, subtree: true });
    window.addEventListener('resize', () => {
      document.querySelectorAll('[data-audio-player].audio-player--wave-ready').forEach((player) => {
        this.drawWave(player, this.playedRatio(player));
      });
    });
  }

  async loadWave(player) {
    const src = player.dataset.audioSrc;
    const canvas = player.querySelector('[data-audio-wave]');
    if (!src || !canvas) return;

    try {
      let peaks = this.peaksCache.get(src);
      if (!peaks) {
        const response = await fetch(src);
        if (!response.ok) return;
        const buffer = await response.arrayBuffer();
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        this.decodeContext = this.decodeContext || new AudioCtx();
        const decoded = await this.decodeContext.decodeAudioData(buffer);
        peaks = this.computePeaks(decoded, 480);
        this.peaksCache.set(src, peaks);
      }
      player.wavePeaks = peaks;
      player.classList.add('audio-player--wave-ready');
      this.drawWave(player, this.playedRatio(player));
    } catch (error) {
      // Decode failed (unsupported codec, network) — the flat fill bar stays as fallback.
    }
  }

  computePeaks(decoded, bucketCount) {
    const data = decoded.getChannelData(0);
    const bucketSize = Math.max(1, Math.floor(data.length / bucketCount));
    const stride = Math.max(1, Math.floor(bucketSize / 64));
    const peaks = new Float32Array(bucketCount);
    let maxPeak = 0.01;

    for (let i = 0; i < bucketCount; i++) {
      const start = i * bucketSize;
      const end = Math.min(start + bucketSize, data.length);
      let peak = 0;
      for (let j = start; j < end; j += stride) {
        const value = Math.abs(data[j]);
        if (value > peak) peak = value;
      }
      peaks[i] = peak;
      if (peak > maxPeak) maxPeak = peak;
    }

    for (let i = 0; i < bucketCount; i++) peaks[i] /= maxPeak;
    return peaks;
  }

  playedRatio(player) {
    if (this.activePlayer !== player || !this.audio || !this.audio.duration) return 0;
    return this.audio.currentTime / this.audio.duration;
  }

  drawWave(player, ratio) {
    const canvas = player.querySelector('[data-audio-wave]');
    const peaks = player.wavePeaks;
    if (!canvas || !peaks) return;

    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const width = Math.round(rect.width * dpr);
    const height = Math.round(rect.height * dpr);
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }

    const styles = getComputedStyle(canvas);
    const baseColor = styles.getPropertyValue('--awave-base').trim() || 'rgba(0, 0, 0, 0.2)';
    const fillColor = styles.getPropertyValue('--awave-fill').trim() || '#1A47FF';

    const barWidth = Math.max(1, Math.round(2 * dpr));
    const gap = Math.max(1, Math.round(1 * dpr));
    const step = barWidth + gap;
    const barCount = Math.max(1, Math.floor(width / step));
    const cutoff = Math.floor(barCount * Math.min(Math.max(ratio || 0, 0), 1));

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);

    for (let i = 0; i < barCount; i++) {
      const peakIndex = Math.floor((i / barCount) * peaks.length);
      const barHeight = Math.max(Math.round(2 * dpr), Math.round(peaks[peakIndex] * height * 0.92));
      const y = Math.round((height - barHeight) / 2);
      ctx.fillStyle = i < cutoff ? fillColor : baseColor;
      ctx.fillRect(i * step, y, barWidth, barHeight);
    }
  }

  ensureAudio() {
    if (this.audio) return this.audio;

    this.audio = new Audio();
    this.audio.preload = 'metadata';
    this.audio.addEventListener('timeupdate', () => this.updateProgress());
    this.audio.addEventListener('loadedmetadata', () => this.updateProgress());
    this.audio.addEventListener('ended', () => this.resetActivePlayer());

    return this.audio;
  }

  handleClick(event) {
    const toggle = event.target.closest('[data-audio-toggle]');
    if (!toggle) return;

    const player = toggle.closest('[data-audio-player]');
    if (!player) return;

    event.preventDefault();
    this.togglePlayer(player);
  }

  handlePointerDown(event) {
    const bar = event.target.closest('[data-audio-progress]');
    if (!bar) return;

    const player = bar.closest('[data-audio-player]');
    if (!player) return;

    event.preventDefault();
    this.scrubbingPlayer = player;
    this.seekFromPointer(player, event);
  }

  handlePointerMove(event) {
    if (!this.scrubbingPlayer) return;
    this.seekFromPointer(this.scrubbingPlayer, event);
  }

  handlePointerUp() {
    this.scrubbingPlayer = null;
  }

  togglePlayer(player) {
    const src = player.dataset.audioSrc;
    if (!src) return;

    if (!player.wavePeaks && !player.waveLoading && player.querySelector('[data-audio-wave]')) {
      player.waveLoading = true;
      this.loadWave(player);
    }

    const audio = this.ensureAudio();
    const isSamePlayer = this.activePlayer === player;

    if (isSamePlayer && !audio.paused) {
      audio.pause();
      this.setState(player, false);
      return;
    }

    if (!isSamePlayer) {
      if (this.activePlayer) this.setState(this.activePlayer, false);
      audio.src = src;
      this.activePlayer = player;
    }

    audio.play().then(() => {
      this.setState(player, true);
      document.dispatchEvent(new CustomEvent('archive:audio-play', { detail: { src, playerId: player.dataset.audioId || null } }));
    }).catch(() => {
      this.setState(player, false);
    });
  }

  setState(player, isPlaying) {
    player.dataset.playing = String(isPlaying);
    const label = player.querySelector('[data-audio-state]');
    const playIcon = player.querySelector('[data-audio-icon-play]');
    const pauseIcon = player.querySelector('[data-audio-icon-pause]');

    if (label) label.textContent = isPlaying ? 'Playing preview' : 'Preview ready';
    if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
    if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
  }

  updateProgress() {
    if (!this.activePlayer || !this.audio) return;

    const duration = this.audio.duration || 0;
    const currentTime = this.audio.currentTime || 0;
    const fill = this.activePlayer.querySelector('[data-audio-fill]');
    const current = this.activePlayer.querySelector('[data-audio-current]');
    const total = this.activePlayer.querySelector('[data-audio-total]');

    if (fill && duration > 0) {
      fill.style.width = `${(currentTime / duration) * 100}%`;
    }

    if (current) current.textContent = this.formatTime(currentTime);
    if (total) total.textContent = this.formatTime(duration);

    if (duration > 0 && this.activePlayer.wavePeaks) {
      this.drawWave(this.activePlayer, currentTime / duration);
    }
  }

  seekFromPointer(player, event) {
    const audio = this.ensureAudio();
    const bar = player.querySelector('[data-audio-progress]');
    if (!bar) return;

    if (this.activePlayer !== player) {
      this.togglePlayer(player);
    }

    if (!audio.duration) return;

    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    audio.currentTime = audio.duration * ratio;
    this.updateProgress();
  }

  resetActivePlayer() {
    if (this.activePlayer) {
      this.setState(this.activePlayer, false);
      const fill = this.activePlayer.querySelector('[data-audio-fill]');
      const current = this.activePlayer.querySelector('[data-audio-current]');

      if (fill) fill.style.width = '0%';
      if (current) current.textContent = this.formatTime(0);
      if (this.activePlayer.wavePeaks) this.drawWave(this.activePlayer, 0);
    }
  }

  formatTime(time) {
    if (!Number.isFinite(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }
}

window.AudioPlayerManager = new AudioPlayerManager();
